import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Reviews.css';

const SERVICE_TYPES = ['All', 'Past Predictions', 'Future Consultation', 'Remedies', 'General'];

// Seed reviews shown until the admin adds real ones via /admin/reviews.
// These render the same way real reviews would so the section never looks empty.
const SEED_REVIEWS = [
  { id: 's1', clientName: 'Rajesh Kumar', service: 'Past Predictions', rating: 5, type: 'text',
    content: 'Tilak garu verified events from my past with frightening accuracy before predicting my future. After that, I knew the guidance ahead was real.',
    timestamp: new Date().toISOString(), seed: true },
  { id: 's2', clientName: 'Priya Sharma',  service: 'Remedies',         rating: 5, type: 'text',
    content: 'The remedy he gave for my marriage delay worked within four months. Simple, classical, and effective. No expensive yantras, no fear.',
    timestamp: new Date().toISOString(), seed: true },
  { id: 's3', clientName: 'Anand Reddy',  service: 'Future Consultation', rating: 5, type: 'text',
    content: 'Career switch timing was exactly as he predicted. The offer came in the month he marked. I would not have had the courage without his reading.',
    timestamp: new Date().toISOString(), seed: true },
];

// ── Auto-rotating carousel ────────────────────────────────────────────────────
const ReviewCarousel = ({ reviews }) => {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  const go = useCallback((next) => {
    setIdx(i => (i + next + reviews.length) % reviews.length);
  }, [reviews.length]);

  useEffect(() => {
    if (reviews.length < 2) return;
    timerRef.current = setInterval(() => go(1), 4000);
    return () => clearInterval(timerRef.current);
  }, [go, reviews.length]);

  if (!reviews.length) return null;
  const r = reviews[idx];
  const firstName = (r.clientName || 'Client').split(' ')[0];

  return (
    <div className="rev-carousel">
      <div className="rev-car-track" key={idx}>
        <div className="rev-car-stars">{'★'.repeat(r.rating || 5)}{'☆'.repeat(5 - (r.rating || 5))}</div>
        <p className="rev-car-text">"{r.content || 'Excellent service!'}"</p>
        <div className="rev-car-meta">
          <span className="rev-car-name">— {firstName}</span>
          <span className="rev-car-service">{r.service || 'Consultation'}</span>
          {r.timestamp && <span className="rev-car-date">{new Date(r.timestamp).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</span>}
        </div>
      </div>
      {reviews.length > 1 && (
        <div className="rev-car-dots">
          {reviews.slice(0, Math.min(reviews.length, 8)).map((_, i) => (
            <button key={i} className={`rev-car-dot${i === idx ? ' active' : ''}`}
              onClick={() => { clearInterval(timerRef.current); setIdx(i); }}
              aria-label={`Review ${i + 1}`} />
          ))}
        </div>
      )}
      <div className="rev-car-nav">
        <button onClick={() => { clearInterval(timerRef.current); go(-1); }} aria-label="Previous">‹</button>
        <button onClick={() => { clearInterval(timerRef.current); go(1);  }} aria-label="Next">›</button>
      </div>
    </div>
  );
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter]   = useState('All');
  const [loading, setLoading] = useState(true);
  const [isSeed, setIsSeed]   = useState(false);

  useEffect(() => {
    fetch('/api/reviews')
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.reviews) && d.reviews.length) {
          setReviews(d.reviews);
        } else {
          setReviews(SEED_REVIEWS);
          setIsSeed(true);
        }
      })
      .catch(() => { setReviews(SEED_REVIEWS); setIsSeed(true); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'All' ? reviews : reviews.filter(r => r.service === filter);
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '5.0';
  const textReviews  = filtered.filter(r => r.type === 'text').slice(0, 6);
  const audioReviews = filtered.filter(r => r.type === 'audio').slice(0, 4);
  const videoReviews = filtered.filter(r => r.type === 'video').slice(0, 4);

  if (loading) return null;

  return (
    <section className="reviews-section" id="client-reviews" aria-label="Client reviews">
      <div className="reviews-header">
        <p className="section-eyebrow">Verified Reviews</p>
        <h2 className="section-title">What Our Clients Share</h2>

        <div className="reviews-rating-summary">
          <div className="reviews-stars-big">{'★'.repeat(Math.round(Number(avgRating)))}{'☆'.repeat(5 - Math.round(Number(avgRating)))}</div>
          <div className="reviews-rating-num">{avgRating}</div>
          <div className="reviews-count">
            {isSeed ? 'New on the app — be one of the first' : `${reviews.length} verified reviews`}
          </div>
        </div>

      </div>

      {/* Auto-rotating carousel — top rated text reviews */}
      <ReviewCarousel reviews={reviews.filter(r => r.type === 'text' && r.rating >= 4).slice(0, 12)} />

      <div className="reviews-header" style={{ marginTop: '2rem' }}>
        <div className="reviews-filters">
          {SERVICE_TYPES.map(s => (
            <button key={s} className={`r-filter${filter === s ? ' r-filter--active' : ''}`} onClick={() => setFilter(s)}>{s}</button>
          ))}
        </div>
      </div>

      {/* Text reviews */}
      {textReviews.length > 0 && (
        <div className="reviews-text-grid">
          {textReviews.map(r => <TextReviewCard key={r.id} review={r} />)}
        </div>
      )}

      {/* Audio reviews */}
      {audioReviews.length > 0 && (
        <div className="reviews-audio-section">
          <h3 className="reviews-sub-heading">🎤 Hear From Our Clients</h3>
          <div className="reviews-audio-list">
            {audioReviews.map(r => <AudioReviewCard key={r.id} review={r} />)}
          </div>
        </div>
      )}

      {/* Video reviews */}
      {videoReviews.length > 0 && (
        <div className="reviews-video-section">
          <h3 className="reviews-sub-heading">▶️ Video Testimonials</h3>
          <div className="reviews-video-grid">
            {videoReviews.map(r => <VideoReviewCard key={r.id} review={r} />)}
          </div>
        </div>
      )}

      <div className="reviews-cta">
        <a href="/reviews" className="reviews-view-all">View All Reviews →</a>
      </div>
    </section>
  );
};

const Stars = ({ n }) => (
  <span className="r-stars" aria-label={`${n} stars`}>{'★'.repeat(n)}{'☆'.repeat(5 - n)}</span>
);

const ShareBtn = ({ review }) => {
  const share = () => {
    const text = `⭐ ${review.rating}/5 — "${review.content?.slice(0, 120)}…"\n— ${review.clientName} (${review.service})\nvtdastrology.com`;
    if (navigator.share) {
      navigator.share({ title: 'VTD Astrology Review', text }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => alert('Copied!')).catch(() => {});
    }
  };
  return <button className="r-share-btn" onClick={share} aria-label="Share review">📤 Share</button>;
};

const TextReviewCard = ({ review: r }) => (
  <article className="r-card">
    <div className="r-card-top">
      <div className="r-avatar">{(r.clientName || '?').charAt(0)}</div>
      <div>
        <p className="r-name">{r.clientName}</p>
        <p className="r-service">{r.service}</p>
      </div>
      <Stars n={r.rating} />
    </div>
    <p className="r-content">"{r.content}"</p>
    <ShareBtn review={r} />
  </article>
);

const AudioReviewCard = ({ review: r }) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };
  return (
    <div className="r-audio-card">
      <div className="r-audio-avatar">{(r.clientName || '?').charAt(0)}</div>
      <div className="r-audio-info">
        <p className="r-name">{r.clientName}</p>
        <p className="r-service">{r.service}</p>
        <Stars n={r.rating} />
      </div>
      <button className="r-play-btn" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? '⏸' : '▶'}
      </button>
      {r.fileUrl && <audio ref={audioRef} src={r.fileUrl} onEnded={() => setPlaying(false)} style={{ display: 'none' }} />}
    </div>
  );
};

const VideoReviewCard = ({ review: r }) => {
  const thumb = getVideoThumb(r.videoUrl);
  return (
    <a className="r-video-card" href={r.videoUrl} target="_blank" rel="noopener noreferrer" aria-label={`Video by ${r.clientName}`}>
      {thumb && <img className="r-video-thumb" src={thumb} alt={r.clientName} />}
      <div className="r-video-play">▶</div>
      <div className="r-video-info">
        <p className="r-name">{r.clientName}</p>
        <Stars n={r.rating} />
      </div>
    </a>
  );
};

function getVideoThumb(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
  if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/mqdefault.jpg`;
  return null;
}

export default Reviews;
